import { effect } from "@preact/signals";
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import BookCollectionStore from "stores/BookCollectionStore";
import BookStore from "stores/BookStore";
import SearchQueryStore from "stores/SearchQueryStore";
import stores from "stores/stores";
import { PublicLibraryList } from "views/PublicLibraryListView";
import SkeletonView from "views/SkeletonView";

interface AppRouterLike {
  navigate(path: string, options?: { trigger?: boolean }): void;
}

interface InfiniteScrollState {
  currentSkip: number;
  pageLimit: number;
  isLoading: boolean;
  hasMore: boolean;
}

interface InfiniteScrollLibraryListProps {
  appRouter?: AppRouterLike;
  collection: BookCollectionStore;
  fetchStateChanged?: (isFetching: boolean) => void;
  searchQuery: SearchQueryStore;
  query?: Record<string, unknown>;
  rendered?: () => void;
}

export class InfiniteScrollLibraryList extends preact.Component<
  InfiniteScrollLibraryListProps,
  InfiniteScrollState
> {
  private disposeBooksEffect?: () => void;
  private disposeSearchQueryChange?: () => void;
  private disposeUserLibraryFilterWatch?: () => void;
  private observer?: IntersectionObserver;
  private sentinelElement?: HTMLDivElement | null;
  /**
   * `state.isLoading` only drives the render; this is the authoritative flag,
   * since `setState` leaves `this.state` stale until the next render.
   */
  private isFetchInFlight = false;
  private isResetPending = false;

  state: InfiniteScrollState = {
    currentSkip: 0,
    pageLimit: 20,
    isLoading: false,
    hasMore: true,
  };

  componentDidMount() {
    this.disposeBooksEffect = effect(() => {
      for (const book of this.props.collection.books.value) {
        // Each book's own signal, so that hearting one re-runs the liked
        // predicate below and the row appears or disappears straight away.
        void book.book.value;
      }
      void this.props.collection.totalCountSignal.value;
      this.forceUpdate();
    });

    this.disposeSearchQueryChange = this.props.searchQuery.onQueryChange(() => {
      this.resetAndFetch();
    });

    this.disposeUserLibraryFilterWatch = stores.user.onChange(
      (user) => [
        user.currentLearningLanguage,
        user.publicSortBooksBy,
        user.publicBooksCategories,
        user.publicBooksFilter,
      ],
      () => {
        this.resetAndFetch();
      }
    );

    this.setupObserver();
    this.fetchMore();
  }

  componentDidUpdate() {
    this.setupObserver();
  }

  componentWillUnmount() {
    this.isResetPending = false;
    this.observer?.disconnect();
    this.disposeBooksEffect?.();
    this.disposeSearchQueryChange?.();
    this.disposeUserLibraryFilterWatch?.();
  }

  /**
   * A fetch already in flight will append to the shared collection whenever it
   * lands, so restarting on top of it would mix the old filter's page into the
   * new results and advance the offset twice. Wait for it instead; repeated
   * filter changes collapse into one reset that reads the latest filters.
   */
  resetAndFetch() {
    if (this.isFetchInFlight) {
      this.isResetPending = true;
      return;
    }

    this.props.fetchStateChanged?.(true);
    this.props.collection.reset();
    this.setState(
      {
        currentSkip: 0,
        isLoading: false,
        hasMore: true,
      },
      () => this.fetchMore()
    );
  }

  /**
   * Clears the in-flight flag and runs a reset deferred by
   * {@link resetAndFetch}, discarding the stale page. Returns true if it did,
   * in which case the caller must not do any further page bookkeeping.
   */
  private onFetchSettled() {
    this.isFetchInFlight = false;
    if (!this.isResetPending) return false;
    this.isResetPending = false;
    this.resetAndFetch();
    return true;
  }

  fetchMore() {
    if (this.isFetchInFlight || !this.state.hasMore) {
      return;
    }

    this.isFetchInFlight = true;
    this.setState({ isLoading: true });
    this.props.fetchStateChanged?.(true);

    const query = {
      language: stores.user.currentLearningLanguage.value,
      firstLanguage: stores.user.firstLanguage.value,
      sortBy: stores.user.state.value.publicSortBooksBy,
      categories: stores.user.state.value.publicBooksCategories.join("|"),
      filter: stores.user.state.value.publicBooksFilter,
      limit: this.state.pageLimit + 1,
      skip: this.state.currentSkip,
      search: this.props.searchQuery.query.value,
      ...this.props.query,
    };

    this.props.collection
      .fetch(query, { append: true })
      .then((fetchedBooks) => {
        if (this.onFetchSettled()) return;

        const fetchedCount = fetchedBooks.length;
        const hasMore = fetchedCount > this.state.pageLimit;

        if (hasMore && this.props.collection.length > 0) {
          const lastBook =
            this.props.collection.books.value[
              this.props.collection.books.value.length - 1
            ];
          if (lastBook) {
            this.props.collection.removeBook(lastBook.id);
          }
        }

        const actualItemsAdded = hasMore ? this.state.pageLimit : fetchedCount;
        const isFirstFetch = this.state.currentSkip === 0;
        this.setState(
          {
            currentSkip: this.state.currentSkip + actualItemsAdded,
            isLoading: false,
            hasMore,
          },
          () => {
            this.props.fetchStateChanged?.(false);
            if (isFirstFetch) {
              this.props.rendered?.();
            }
          }
        );
      })
      .catch(() => {
        if (this.onFetchSettled()) return;

        this.props.fetchStateChanged?.(false);
        this.setState({ isLoading: false });
      });
  }

  setupObserver() {
    if (!this.sentinelElement) return;

    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.fetchMore();
          }
        }
      },
      { rootMargin: "200px" }
    );
    this.observer.observe(this.sentinelElement);
  }

  likedOnly() {
    return stores.user.state.value.publicBooksFilter?.likedOnly ?? false;
  }

  /**
   * In Liked mode the server only ever sends hearted texts, so a text the user
   * unhearts here has to leave the list at once. Filtering on render rather
   * than dropping it from the collection keeps the fetch offset in step.
   */
  bookFilter = (book: BookStore) =>
    !this.likedOnly() || (book.book.value.yourVote ?? 0) > 0;

  renderStatus() {
    const hasBooks = this.props.collection.books.value.some(this.bookFilter);
    const searchQuery = this.props.searchQuery.query.value;

    if (this.state.isLoading) {
      return <SkeletonView kind="cards" count={hasBooks ? 1 : 4} />;
    }

    if (!hasBooks) {
      if (this.likedOnly()) {
        return (
          <div class="infiniteScrollEmpty">
            <p>No liked texts match these filters.</p>
          </div>
        );
      }

      return (
        <div class="infiniteScrollEmpty">
          <p>Sorry, no results found.</p>
          {searchQuery ? (
            <p>
              If you find what you're looking for elsewhere, you can{" "}
              <a href="/upload">upload it to Readlang</a>
              {/* The Websites page, which explains the Web Reader (the iOS
                  app's browser has it built in) */}
              {deviceDetect.isIOSApp() ? null : (
                <>
                  , or{" "}
                  <a
                    href={`/${stores.user.currentLearningLanguage.value}/links`}
                  >
                    read it on its website
                  </a>
                </>
              )}
              .
            </p>
          ) : null}
        </div>
      );
    }

    if (!this.state.hasMore) {
      return (
        <div class="infiniteScrollEnd">
          <p>No more results</p>
        </div>
      );
    }

    return null;
  }

  render() {
    return (
      <>
        <div data-subview="libraryList">
          <PublicLibraryList
            books={this.props.collection}
            bookFilter={this.bookFilter}
            rendered={undefined}
            showNoTextsMessage={false}
          />
        </div>
        <div
          class="infiniteScrollSentinel"
          ref={(element) => {
            this.sentinelElement = element;
          }}
        >
          {this.renderStatus()}
        </div>
      </>
    );
  }
}
